'use client';

import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { useCallback, useEffect, useRef } from 'react';

interface ReferInfo {
  refer_page_name: string;
  refer_url_path: string;
}

/**
 * Fit Guide页面埋点追踪Hook
 * 使用项目统一的埋点上报系统
 */
export const useFitGuideTracking = () => {
  const { trackEvent } = useReporting();
  const hasTrackedPageView = useRef(false);
  const { getFromRouterInfo } = useRouteInfo();
  /**
   * 神策埋点：Fit Guide页面浏览
   * @TrackType sensorsdata
   * @EventType FitGuidePage_View
   * @EventDesc Fit Guide页面浏览
   * @EventParams {
   *   refer_page_name: 来源页面名称,
   *   refer_url_path: 来源页面路径,
   * }
   */
  const trackPageView = useCallback(
    (referInfo?: ReferInfo) => {
      const { refer_page_name, refer_url_path } = getFromRouterInfo();
      try {
        const trackingReferInfo = referInfo || { refer_page_name, refer_url_path };

        trackEvent(
          {
            eventName: 'FitGuidePage_View',
            properties: {
              refer_page_name: trackingReferInfo.refer_page_name,
              refer_url_path: trackingReferInfo.refer_url_path,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('FitGuidePage_View tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：Fit Guide页面资源位曝光
   * @TrackType sensorsdata
   * @EventType FitGuidePageMkt_Exp
   * @EventDesc Fit Guide页面资源位曝光
   * @EventParams {
   *   mkt_name: 营销位名称,
   *   pits_index: 坑位索引,
   *   pits_jump_type: 跳转类型,
   *   pits_jump_content: 跳转内容,
   * }
   */
  const trackPageExposure = useCallback(
    (mkt_name: string, pits_index: number, pits_jump_type?: string, pits_jump_content?: string) => {
      try {
        const properties: any = {
          mkt_name,
          pits_index,
        };

        if (pits_jump_type) {
          properties.pits_jump_type = pits_jump_type;
        }
        if (pits_jump_content) {
          properties.pits_jump_content = pits_jump_content;
        }

        trackEvent(
          {
            eventName: 'FitGuidePageMkt_Exp',
            properties,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('FitGuidePageMkt_Exp tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：Fit Guide页面营销位点击
   * @TrackType sensorsdata
   * @EventType FitGuidePageMkt_Click
   * @EventDesc Fit Guide页面营销位点击
   * @EventParams {
   *   mkt_name: 营销位名称,
   *   pits_index: 坑位索引,
   *   pits_jump_type: 跳转类型,
   *   pits_jump_content: 跳转内容/链接,
   * }
   * description: 点击下载可以测试此处埋点
   */
  const trackMktClick = useCallback(
    (
      mkt_name = 'Frame Cutouts',
      pits_index = 0,
      pits_jump_type = '4',
      pits_jump_content?: string,
    ) => {
      try {
        trackEvent(
          {
            eventName: 'FitGuidePageMkt_Click',
            properties: {
              mkt_name,
              pits_index,
              pits_jump_type,
              pits_jump_content: pits_jump_content || '',
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('FitGuidePageMkt_Click tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 获取来源信息
   * 优先使用统一路由信息Hook提供的数据
   */
  const getReferInfo = () => {
    const { refer_page_name, refer_url_path } = getFromRouterInfo();
    return {
      refer_page_name,
      refer_url_path,
    };
  };

  // 页面进入时的埋点
  useEffect(() => {
    if (!hasTrackedPageView.current) {
      trackPageView();
      hasTrackedPageView.current = true;
    }
  }, [trackPageView]);

  // 页面离开时的处理
  useEffect(() => {
    const handleBeforeUnload = () => {
      // 这里可以处理页面离开时的逻辑
      // 在Next.js中，路由缓存通常由框架自动处理
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, []);

  return {
    trackPageView,
    trackPageExposure,
    trackMktClick,
    getReferInfo,
  };
};
